/**
 * Composable metric rendering primitives for CLI output.
 *
 * This module provides small, pure functions that produce colored terminal strings
 * for token counts, costs, elapsed time, and finish reasons. Each function is
 * independently testable and designed to be combined via {@link joinParts}.
 *
 * **Design principles:**
 * - Pure functions with no side effects
 * - Consistent color scheme across all metric types
 * - Reuses {@link formatTokens} and {@link formatCost} from `metric-formatters.ts`
 * - Follows the same conventions as `formatters.ts` and `block-formatters.ts`
 *
 * **Color scheme:**
 * - Yellow: Input tokens (↑)
 * - Blue: Cached tokens (⟳)
 * - Green: Output tokens (↓)
 * - Magenta: Reasoning tokens (💭) and cache-creation tokens (✎)
 * - Cyan: Cost ($)
 * - Dim: Icons, time, separators
 */

import chalk from "chalk";
import { formatCost, formatTokens } from "./metric-formatters.js";

// ─────────────────────────────────────────────────────────────────────────────
// Token direction type
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Direction (role) of a token count within an LLM call.
 *
 * - `"input"` — tokens sent to the model (↑ yellow)
 * - `"cached"` — tokens served from the prompt cache (⟳ blue)
 * - `"output"` — tokens generated by the model (↓ green)
 * - `"reasoning"` — internal chain-of-thought tokens (💭 magenta)
 * - `"cacheCreation"` — tokens written to the prompt cache (✎ magenta)
 */
export type TokenDirection = "input" | "cached" | "output" | "reasoning" | "cacheCreation";

/** Optional overrides for {@link tokenPart}. */
export interface TokenPartOptions {
  /**
   * When `true`, prepends a `~` tilde to signal the count is an estimate.
   * @default false
   */
  estimated?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Individual part renderers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Renders a single token-count metric part with direction-appropriate color and icon.
 *
 * **Icon / color mapping:**
 * | direction       | icon | color   |
 * |-----------------|------|---------|
 * | `"input"`       | ↑    | yellow  |
 * | `"cached"`      | ⟳    | blue    |
 * | `"output"`      | ↓    | green   |
 * | `"reasoning"`   | 💭   | magenta |
 * | `"cacheCreation"` | ✎  | magenta |
 *
 * @param direction - The role of these tokens in the LLM call.
 * @param tokens - Raw token count.
 * @param opts - Optional rendering overrides.
 * @returns Colored terminal string, e.g. `"↑ 10.4k"` or `"⟳ 3.0k"`.
 *
 * @example
 * ```typescript
 * tokenPart("input", 10400)            // "↑ 10.4k" (dim ↑, yellow count)
 * tokenPart("cached", 3000)            // "⟳ 3.0k"  (dim ⟳, blue count)
 * tokenPart("output", 49)              // "↓ 49"     (dim ↓, green count)
 * tokenPart("reasoning", 512)          // "💭 512"   (dim 💭, magenta count)
 * tokenPart("cacheCreation", 2048)     // "✎ 2.0k"  (dim ✎, magenta count)
 * tokenPart("input", 5200, { estimated: true }) // "↑ ~5.2k"
 * ```
 */
export function tokenPart(
  direction: TokenDirection,
  tokens: number,
  opts?: TokenPartOptions,
): string {
  const prefix = opts?.estimated ? "~" : "";
  const formatted = `${prefix}${formatTokens(tokens)}`;

  switch (direction) {
    case "input":
      return chalk.dim("↑") + chalk.yellow(` ${formatted}`);
    case "cached":
      return chalk.dim("⟳") + chalk.blue(` ${formatted}`);
    case "output":
      return chalk.dim("↓") + chalk.green(` ${formatted}`);
    case "reasoning":
      return chalk.dim("💭") + chalk.magenta(` ${formatted}`);
    case "cacheCreation":
      return chalk.dim("✎") + chalk.magenta(` ${formatted}`);
  }
}

/**
 * Renders a cost value as a cyan `$`-prefixed string.
 *
 * Delegates precision formatting to {@link formatCost} so the number of
 * decimal places automatically scales with the magnitude of the cost.
 *
 * @param cost - Cost in USD.
 * @returns Cyan terminal string, e.g. `"$0.0032"`.
 *
 * @example
 * ```typescript
 * costPart(0.0032)   // "$0.0032" (cyan)
 * costPart(1.5)      // "$1.50"   (cyan)
 * costPart(0.00009)  // "$0.00009" (cyan)
 * ```
 */
export function costPart(cost: number): string {
  return chalk.cyan(`$${formatCost(cost)}`);
}

/**
 * Renders an elapsed-time value as a dim seconds string.
 *
 * @param seconds - Elapsed time in seconds.
 * @returns Dim terminal string, e.g. `"24.8s"`.
 *
 * @example
 * ```typescript
 * timePart(24.8)   // "24.8s" (dim)
 * timePart(3.0)    // "3.0s"  (dim)
 * timePart(0)      // "0.0s"  (dim)
 * ```
 */
export function timePart(seconds: number): string {
  return chalk.dim(`${seconds.toFixed(1)}s`);
}

/**
 * Renders a finish reason with appropriate color.
 *
 * Normal completion reasons (`"stop"` and `"end_turn"`, case-insensitive) are
 * rendered green. All other reasons (e.g. `"length"`, `"tool_calls"`,
 * `"max_tokens"`) are rendered yellow to draw attention.
 *
 * The reason is always uppercased for visual clarity.
 *
 * @param reason - Raw finish reason string from the provider.
 * @returns Green or yellow terminal string, e.g. `"STOP"` or `"LENGTH"`.
 *
 * @example
 * ```typescript
 * finishReasonPart("stop")      // "STOP"     (green)
 * finishReasonPart("end_turn")  // "END_TURN" (green)
 * finishReasonPart("length")    // "LENGTH"   (yellow)
 * finishReasonPart("STOP")      // "STOP"     (green, case-insensitive)
 * ```
 */
export function finishReasonPart(reason: string): string {
  const upper = reason.toUpperCase();
  if (upper === "STOP" || upper === "END_TURN") {
    return chalk.green(upper);
  }
  return chalk.yellow(upper);
}

// ─────────────────────────────────────────────────────────────────────────────
// Composite helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Options for {@link buildTokenMetrics}. */
export interface BuildTokenMetricsOptions {
  /** Input token count (↑ yellow). Omit or pass `undefined`/`0` to skip. */
  input?: number;
  /** Cached token count (⟳ blue). Omit or pass `undefined`/`0` to skip. */
  cached?: number;
  /** Output token count (↓ green). Omit or pass `undefined`/`0` to skip. */
  output?: number;
  /** Reasoning token count (💭 magenta). Omit or pass `undefined`/`0` to skip. */
  reasoning?: number;
  /** Cache-creation token count (✎ magenta). Omit or pass `undefined`/`0` to skip. */
  cacheCreation?: number;
  /** When `true`, marks input and output counts as estimated (prefixed with `~`). */
  estimated?: { input?: boolean; output?: boolean };
}

/**
 * Assembles an array of token metric part strings from a set of optional counts.
 *
 * Fields that are `undefined`, `null`, or `0` are omitted from the result so
 * the caller never sees empty entries. The parts are returned in canonical order:
 * input → cached → output → reasoning → cacheCreation.
 *
 * @param opts - Optional token counts to include.
 * @returns Array of colored terminal strings (may be empty).
 *
 * @example
 * ```typescript
 * buildTokenMetrics({ input: 10400, cached: 3000, output: 49 })
 * // ["↑ 10.4k", "⟳ 3.0k", "↓ 49"]
 *
 * buildTokenMetrics({ input: 896, output: 200, reasoning: 64 })
 * // ["↑ 896", "↓ 200", "💭 64"]
 *
 * buildTokenMetrics({})   // []
 * buildTokenMetrics({ input: 0, output: 0 })  // []
 * ```
 */
export function buildTokenMetrics(opts: BuildTokenMetricsOptions): string[] {
  const parts: string[] = [];

  if (opts.input && opts.input > 0) {
    parts.push(tokenPart("input", opts.input, { estimated: opts.estimated?.input }));
  }
  if (opts.cached && opts.cached > 0) {
    parts.push(tokenPart("cached", opts.cached));
  }
  if (opts.output && opts.output > 0) {
    parts.push(tokenPart("output", opts.output, { estimated: opts.estimated?.output }));
  }
  if (opts.reasoning && opts.reasoning > 0) {
    parts.push(tokenPart("reasoning", opts.reasoning));
  }
  if (opts.cacheCreation && opts.cacheCreation > 0) {
    parts.push(tokenPart("cacheCreation", opts.cacheCreation));
  }

  return parts;
}

/**
 * Joins an array of metric part strings with a dim `|` separator.
 *
 * Empty arrays return an empty string. `undefined` or empty string entries in
 * the input array are filtered out before joining so callers don't need to
 * guard against them.
 *
 * @param parts - Array of metric strings to join.
 * @returns Single string with dim ` | ` separators, or `""` if `parts` is empty.
 *
 * @example
 * ```typescript
 * joinParts(["↑ 10.4k", "↓ 49", "24.8s"])
 * // "↑ 10.4k | ↓ 49 | 24.8s"  (with dim separators)
 *
 * joinParts([])   // ""
 * joinParts(["single"])  // "single"
 * ```
 */
export function joinParts(parts: string[]): string {
  const filtered = parts.filter((p) => p.length > 0);
  if (filtered.length === 0) return "";
  return filtered.join(chalk.dim(" | "));
}
